import { useEffect, useMemo, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { useDarkMode } from "../../../../../context/ThemeContext";
import { useAlert } from "../../../../../context/AlertContext";
import { useGroupsStore } from "../../../../../store/admin/groupsStore";
import { useGroupsList } from "../../../../../features/catalogs/queries";
import { useAllDevices } from "../../../../../features/devices/queries";
import { useAllUsers } from "../../../../../features/users/queries";
import { MONITOR_ROLE_ID } from "../../../../../hooks/useIsMonitor";
import { qk } from "../../../../../lib/queryKeys";
import { useCompaniesStore } from "../../../../../store/admin/companiesStore";
import type { ThemeClasses, Group } from "./types";
import { GroupHeader } from "./components/GroupHeader";
import { GroupList } from "./components/GroupList";
import { GroupPagination } from "./components/GroupPagination";
import { GroupModal, type GroupFormData } from "./components";
import { GroupDetailsModal } from "./components/GroupDetailsModal";

const scrollbarStyles = `
  .scrollbar-light::-webkit-scrollbar {
    width: 6px;
  }
  .scrollbar-light::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }
  .scrollbar-light::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
  .scrollbar-light::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
  
  .scrollbar-dark::-webkit-scrollbar {
    width: 6px;
  }
  .scrollbar-dark::-webkit-scrollbar-track {
    background: #374151;
    border-radius: 3px;
  }
  .scrollbar-dark::-webkit-scrollbar-thumb {
    background: #6b7280;
    border-radius: 3px;
  }
  .scrollbar-dark::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }
`;

export default function GroupsSection() {
  const { isDarkMode } = useDarkMode();
  const { showAlert } = useAlert();
  const { t } = useTranslation("adminSections");
  const {
    createGroup,
    updateGroup,
    deleteGroup,
  } = useGroupsStore();
  const devicesQuery = useAllDevices();
  const devices = devicesQuery.data ?? [];
  const devicesLoading = devicesQuery.isLoading;
  const usersQuery = useAllUsers();
  const users = usersQuery.data ?? [];

  const monitoristas = users.filter(
    (user) => user.role_id === MONITOR_ROLE_ID,
  );

  // Cleanup scroll lock on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  // Estados de filtros
  const [status_id, setStatus_id] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [selectedView, setSelectedView] = useState<"grid" | "list">("list");
  const itemsPerPage = window.innerWidth >= 1536 ? 8 : 6;

  const filterParams = useMemo(
    () => ({
      page: currentPage,
      limit: itemsPerPage,
      status_id: status_id !== "" ? status_id : undefined,
      search: searchQuery,
    }),
    [currentPage, itemsPerPage, status_id, searchQuery],
  );

  const groupsQuery = useGroupsList(filterParams);
  const groups = groupsQuery.data?.data ?? [];
  const meta = groupsQuery.data?.meta;
  const loading = groupsQuery.isLoading;

  const queryClient = useQueryClient();
  const companyId = useCompaniesStore((s) => s.companyId);
  const invalidateGroups = () => {
    queryClient.invalidateQueries({ queryKey: ["groups"] });
    queryClient.invalidateQueries({ queryKey: qk.counts(companyId) });
  };

  // Estados del modal
  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editGroup, setEditGroup] = useState<Group | null>(null);
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  // Estado del modal de detalles
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);

  // Form hook
  const form = useForm<GroupFormData>({
    mode: "onChange",
    defaultValues: {
      name: "",
      description: "",
      status_id: "",
      devices_ids: [],
      monitorista_ids: [],
    },
  });

  // Theme classes
  const themeClasses: ThemeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-white",
    cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
    buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
    buttonText: isDarkMode ? "text-gray-400" : "text-gray-600",
    buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
  };

  // Handlers de filtros
  const handleStatusChange = (value: string | null) => {
    setStatus_id(value || "");
    setCurrentPage(1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  // Handlers de vista
  const handleViewChange = (view: "grid" | "list") => {
    setSelectedView(view);
  };

  // Handlers de modal
  const handleAddNew = () => {
    form.reset({
      name: "",
      description: "",
      status_id: "",
      devices_ids: [],
      monitorista_ids: [],
    });
    setIsEdit(false);
    setEditGroup(null);
    setShowModal(true);
    document.body.style.overflow = "hidden";
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setIsEdit(false);
    setEditGroup(null);
    setLoadingSubmit(false);
    form.reset();
    document.body.style.overflow = "";
  };

  const handleEdit = useCallback(
    (group: Group) => {
      form.reset({
        name: group.name,
        description: group.description || "",
        status_id: group.status_id,
        devices_ids: group.devices_ids || [],
        monitorista_ids: group.monitorista_ids || [],
      });
      setEditGroup(group);
      setIsEdit(true);
      setShowModal(true);
      document.body.style.overflow = "hidden";
    },
    [form]
  );

  const handleViewDetails = useCallback((group: Group) => {
    setSelectedGroup(group);
    setShowDetailsModal(true);
    document.body.style.overflow = "hidden";
  }, []);

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedGroup(null);
    document.body.style.overflow = "";
  };

  const handleEditFromDetails = () => {
    if (selectedGroup) {
      handleCloseDetailsModal();
      handleEdit(selectedGroup);
    }
  };

  const handleDelete = useCallback(
    async (group: Group) => {
      const result = await Swal.fire({
        title: t("groups.alerts.deleteConfirmTitle"),
        text: t("groups.alerts.deleteConfirmText", { name: group.name }),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#ef4444",
        cancelButtonColor: "#6b7280",
        confirmButtonText: t("groups.alerts.deleteConfirmButton"),
        cancelButtonText: t("groups.alerts.cancelButton"),
      });

      if (!result.isConfirmed) return;

      const success = await deleteGroup(group.id);
      if (success.success) {
        showAlert({
          title: t("groups.alerts.success"),
          text: t("groups.alerts.deleteSuccess"),
          icon: "success",
        });
        invalidateGroups();
      } else {
        showAlert({
          title: t("groups.alerts.error"),
          text: success.error || t("groups.alerts.deleteError"),
          icon: "error",
        });
      }
    },
    [deleteGroup, showAlert, invalidateGroups, t]
  );

  const handleSubmit = async (data: GroupFormData) => {
    setLoadingSubmit(true);

    try {
      if (isEdit && editGroup) {
        const updateData = {
          name: data.name,
          description: data.description,
          status_id: data.status_id,
          devices_ids: data.devices_ids,
          monitorista_ids: data.monitorista_ids,
        };

        const result = await updateGroup(editGroup.id, updateData);

        if (result.success) {
          showAlert({
            title: t("groups.alerts.success"),
            text: t("groups.alerts.updateSuccess"),
            icon: "success",
          });
          handleCloseModal();
          invalidateGroups();
        } else {
          showAlert({
            title: t("groups.alerts.error"),
            text: result.error || t("groups.alerts.updateError"),
            icon: "error",
          });
        }
      } else {
        const createData = {
          name: data.name,
          description: data.description,
          status_id: data.status_id,
          devices_ids: data.devices_ids,
          monitorista_ids: data.monitorista_ids,
        };

        const result = await createGroup(createData);

        if (result.success) {
          showAlert({
            title: t("groups.alerts.success"),
            text: t("groups.alerts.createSuccess"),
            icon: "success",
          });
          handleCloseModal();
          invalidateGroups();
        } else {
          showAlert({
            title: t("groups.alerts.error"),
            text: result.error || t("groups.alerts.createError"),
            icon: "error",
          });
        }
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      showAlert({
        title: t("groups.alerts.error"),
        text: t("groups.alerts.processError"),
        icon: "error",
      });
    } finally {
      setLoadingSubmit(false);
    }
  };

  return (
    <>
      <style>{scrollbarStyles}</style>
      <div className="h-full flex flex-col">
        {/* Header */}
        <GroupHeader
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          status_id={status_id}
          searchQuery={searchQuery}
          selectedView={selectedView}
          onStatusChange={handleStatusChange}
          onSearchChange={handleSearchChange}
          onViewChange={handleViewChange}
          onAddNew={handleAddNew}
        />

        {/* Content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden min-w-0 p-3 sm:p-6">
          <GroupList
            groups={groups}
            loading={loading}
            selectedView={selectedView}
            themeClasses={themeClasses}
            isDarkMode={isDarkMode}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onViewDetails={handleViewDetails}
          />

          {/* Paginación */}
          <GroupPagination
            currentPage={currentPage}
            totalPages={meta?.totalPages || 0}
            isDarkMode={isDarkMode}
            onPageChange={setCurrentPage}
          />
        </div>

        {/* Modal de Creación/Edición */}
        <GroupModal
          isOpen={showModal}
          isEdit={isEdit}
          loadingSubmit={loadingSubmit}
          form={form}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          devices={devices}
          devicesLoading={devicesLoading}
          monitoristas={monitoristas}
          monitoristasLoading={false}
          onSubmit={handleSubmit}
          onClose={handleCloseModal}
        />

        {/* Modal de Detalles */}
        <GroupDetailsModal
          isOpen={showDetailsModal}
          group={selectedGroup}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          devices={devices}
          monitoristas={monitoristas}
          onClose={handleCloseDetailsModal}
          onEdit={handleEditFromDetails}
        />
      </div>
    </>
  );
}
